import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import api, { type User, type UserTransaction } from '../../service/api';

interface ManagerUserTransactionsModalProps {
  show: boolean;
  onHide: () => void;
  user: User | null;
}

const PAGE_SIZE = 15;

export default function ManagerUserTransactionsModal({
  show,
  onHide,
  user,
}: ManagerUserTransactionsModalProps) {
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState<UserTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [nextPage, setNextPage] = useState<number | null>(1);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const resetState = useCallback(() => {
    setTransactions([]);
    setError(null);
    setHasMore(true);
    setNextPage(1);
  }, []);

  const loadPage = useCallback(
    async (pageToLoad: number, replace = false) => {
      if (!user) return;
      setIsLoading(true);
      setError(null);

      try {
        const response = await api.transactionsGetForUser(
          user._id,
          pageToLoad,
          PAGE_SIZE,
        );

        setTransactions(prevTransactions =>
          replace ? response.transactions : [...prevTransactions, ...response.transactions],
        );
        setHasMore(response.hasMore);
        setNextPage(response.nextPage);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : t('manager.users.transactions.genericError'),
        );
      } finally {
        setIsLoading(false);
      }
    },
    [t, user],
  );

  useEffect(() => {
    if (show && user) {
      resetState();
      void loadPage(1, true);
    }
  }, [show, user, loadPage, resetState]);

  useEffect(() => {
    if (!show || !hasMore || !nextPage) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(entries => {
      const [entry] = entries;
      if (entry.isIntersecting && !isLoading) {
        void loadPage(nextPage);
      }
    });

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [show, hasMore, nextPage, isLoading, loadPage]);

  useEffect(() => {
    if (!show) {
      resetState();
    }
  }, [show, resetState]);

  const handleRetry = () => {
    if (!user) return;
    const targetPage = transactions.length === 0 ? 1 : nextPage ?? 1;
    void loadPage(targetPage, transactions.length === 0);
  };

  const title = useMemo(() => {
    if (!user) return t('manager.users.transactions.title');
    return t('manager.users.transactions.titleForUser', {
      name: `${user.given_name} ${user.family_name}`,
    });
  }, [t, user]);

  return (
    <Modal show={show} onHide={onHide} centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!user && (
          <p className="text-muted mb-0">
            {t('manager.users.transactions.noUserSelected')}
          </p>
        )}

        {user && (
          <div className="d-flex flex-column gap-3">
            {error && (
              <Alert variant="danger" className="mb-0">
                <div className="d-flex justify-content-between align-items-center gap-3">
                  <span>{error}</span>
                  <Button variant="outline-light" size="sm" onClick={handleRetry}>
                    {t('manager.users.transactions.retry')}
                  </Button>
                </div>
              </Alert>
            )}

            {transactions.length === 0 && !isLoading && !error && (
              <p className="text-muted text-center mb-0">
                {t('manager.users.transactions.empty')}
              </p>
            )}

            <div className="d-flex flex-column gap-3">
              {transactions.map(transaction => {
                const isIncoming = transaction.destination._id === user._id;
                const amountPrefix = isIncoming ? '+' : '-';
                const amountClass = isIncoming ? 'text-success' : 'text-danger';
                const counterparty = isIncoming
                  ? transaction.source
                  : transaction.destination;
                const counterpartyName = `${counterparty.given_name} ${counterparty.family_name}`;
                const timestamp = DateTime.isDateTime(transaction.createdAt)
                  ? transaction.createdAt
                  : DateTime.fromISO(String(transaction.createdAt));

                return (
                  <div
                    key={transaction._id}
                    className="border rounded p-3 shadow-sm"
                  >
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div>
                        <div className="fw-semibold">
                          {transaction.description}
                        </div>
                        <div className="text-muted small">
                          {isIncoming
                            ? t('manager.users.transactions.receivedFrom', {
                                name: counterpartyName,
                              })
                            : t('manager.users.transactions.sentTo', {
                                name: counterpartyName,
                              })}
                        </div>
                        <div className="text-muted small">
                          {timestamp.toLocaleString(DateTime.DATETIME_MED)}
                        </div>
                      </div>
                      <div className={`fw-bold fs-5 ${amountClass}`}>
                        {amountPrefix}
                        {transaction.amount}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div ref={sentinelRef} />

            {isLoading && (
              <div className="text-center py-2">
                <Spinner animation="border" role="status" />
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('common.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
